import { createFileRoute, useRouterState } from '@tanstack/react-router';
import { useAuth } from '@clerk/clerk-react';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { useCallback, useMemo, useRef, useState } from 'react';
import { FiX } from 'react-icons/fi';
import { MessageBubble } from '@/components/MessageBubble';
import { apiJson } from '@/lib/api';
import type { AdminUserDetailResponse, MessageListResponse } from '@/lib/types';

function AdminUserChatPage() {
  const { userId } = Route.useParams();
  const id = Number(userId);
  const { getToken } = useAuth();
  const searchStr = useRouterState({ select: (s) => s.location.search });
  const hid = useMemo(() => new URLSearchParams(searchStr).get('hid') ?? '', [searchStr]);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const [imageViewerUrl, setImageViewerUrl] = useState<string | null>(null);

  const userQuery = useQuery({
    queryKey: ['admin', 'user-detail', id],
    queryFn: () => apiJson<AdminUserDetailResponse>(getToken, `/v1/admin/users/${id}`),
  });

  const hidNum = Number(hid);
  const canView =
    !!hid &&
    Number.isFinite(hidNum) &&
    (userQuery.data?.households.some((h) => h.household_id === hidNum) ?? false);

  const getMessagesEndpoint = useCallback(
    (beforeTimestamp?: string) => {
      const limit = 50;
      const params = new URLSearchParams({ limit: String(limit) });
      if (beforeTimestamp) params.set('before_timestamp', beforeTimestamp);
      return `/v1/admin/households/${hidNum}/messages?${params.toString()}`;
    },
    [hidNum],
  );

  const messagesQuery = useInfiniteQuery({
    queryKey: ['admin', 'household-messages', hidNum],
    queryFn: async ({ pageParam }) =>
      apiJson<MessageListResponse>(getToken, getMessagesEndpoint(pageParam ?? undefined)),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => {
      if (!lastPage.has_more || lastPage.messages.length === 0) return null;
      const oldest = lastPage.messages[lastPage.messages.length - 1];
      return oldest.timestamp;
    },
    refetchInterval: 15000,
    enabled: canView,
  });

  const pages = messagesQuery.data?.pages ?? [];
  const messages = pages.flatMap((p) => p.messages);
  const hasMore = pages.length > 0 && pages[pages.length - 1]?.has_more;
  const totalMessageCount = pages[0]?.total_count;

  const handleScroll = useCallback(() => {
    const container = messagesContainerRef.current;
    if (!container) return;
    if (container.scrollTop < 100 && hasMore && !messagesQuery.isFetchingNextPage) {
      messagesQuery.fetchNextPage();
    }
  }, [hasMore, messagesQuery]);

  if (!hid || !canView) {
    return <p className='text-sm text-gray-500'>Select a valid household (use the household chips above).</p>;
  }

  return (
    <div className='flex flex-col min-h-[480px]'>
      <div className='mb-4 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1'>
        <h2 className='text-lg font-semibold text-gray-900'>
          Messages · household #{hidNum}
        </h2>
        {messagesQuery.isLoading ? (
          <span className='text-sm text-gray-400 tabular-nums'>…</span>
        ) : totalMessageCount != null ? (
          <span className='text-sm font-medium text-gray-600 tabular-nums'>
            {totalMessageCount.toLocaleString()} message{totalMessageCount === 1 ? '' : 's'}
          </span>
        ) : null}
      </div>
      <div
        ref={messagesContainerRef}
        onScroll={handleScroll}
        className='flex-1 overflow-y-auto px-1 py-2 flex flex-col-reverse bg-white rounded-lg border border-gray-200 min-h-[360px] max-h-[70vh]'
      >
        {messagesQuery.isLoading ? (
          <div className='flex justify-center py-8'>
            <div className='animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full' />
          </div>
        ) : messages.length === 0 ? (
          <p className='text-center text-gray-500 py-8'>No messages yet.</p>
        ) : (
          messages.map((m) => (
            <MessageBubble
              key={m.id}
              message={m}
              currentUserId={id}
              onImagePress={(url) => setImageViewerUrl(url)}
            />
          ))
        )}
      </div>

      {imageViewerUrl ? (
        <button
          type='button'
          className='fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4'
          onClick={() => setImageViewerUrl(null)}
          aria-label='Close image'
        >
          <FiX className='absolute top-4 right-4 w-8 h-8 text-white' />
          <img src={imageViewerUrl} alt='' className='max-w-full max-h-full object-contain' />
        </button>
      ) : null}
    </div>
  );
}

export const Route = createFileRoute('/admin/users/$userId/chat')({
  validateSearch: (search: Record<string, unknown>) => ({
    hid: typeof search.hid === 'string' ? search.hid : undefined,
  }),
  component: AdminUserChatPage,
});
